import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { trainerApi } from '@/api/trainer'
import { BookOpen, Clock, ChevronRight } from 'lucide-react'
import { formatCourseDuration } from '@/lib/duration'

export default function TrainerCoursesPage({ basePath = '/trainer' }: { basePath?: string }) {
  const navigate = useNavigate()

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['trainer-courses'],
    queryFn: () => trainerApi.getMyCourses().then(r => r.data),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">My Courses</h1>
        <p className="text-sm text-gray-500 mt-0.5">Courses you're qualified to deliver.</p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : courses.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <BookOpen size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">No courses assigned</p>
          <p className="text-sm text-gray-400 mt-1">Your administrator hasn't added any course qualifications yet.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {courses.map(c => (
            <button
              key={c.courseId}
              type="button"
              onClick={() => navigate(`${basePath}/courses/${c.courseId}`)}
              className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-indigo-200 hover:shadow-sm transition-all group w-full"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">
                      {c.name}
                    </h3>
                    {c.code && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {c.code}
                      </span>
                    )}
                    {c.category && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                        {c.category}
                      </span>
                    )}
                  </div>
                  {c.description && (
                    <p className="text-sm text-gray-500 mt-1.5 line-clamp-2">{c.description}</p>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1 text-xs text-gray-400">
                  {formatCourseDuration(c, { compact: true }) && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatCourseDuration(c, { compact: true })}
                    </span>
                  )}
                  {c.defaultExpiryMonths != null && (
                    <span>Expires: {c.defaultExpiryMonths} months</span>
                  )}
                  <ChevronRight size={16} className="mt-1 text-gray-300 group-hover:text-indigo-400 transition-colors" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
